// Datasources — the catalogs an instance attaches (`06` §3.6,
// `SC-F400`–`SC-F416`).
//
// Every type here is the shape `internal/datasources` actually answers with —
// `form.go`'s `Form` and `View`, `health.go`'s panel, `check.go`'s report,
// `files.go`, `sample.go` — because those JSON tags are the wire contract, and a
// client type that guesses is a client that renders a field nothing populates.
//
// Four product rules this module encodes, all of them from `concept.md` §7
// principle 8 (honest labels):
//
//   - **`check` is not `test-connection`.** `sluice datasource check` verifies
//     only that a registered driver factory exists for `spec.type` and reports
//     the declared filters; it opens no socket. The route name, the job type, the
//     OpenAPI summary and the UI label all say *validate configuration*, and
//     [CheckReport] has no field a connection result could be read out of — no
//     latency, no `reachable`, no `probed_at`. Its `checked`, `not_checked` and
//     `notice` prose travel with every answer, so a screen literally cannot
//     present it as a connection test.
//   - **`schemas`, `tables` and `healthCheck.*` parse and are read by nothing.**
//     The write form has no field for them at all, and every read carries
//     [DataSource.not_enforced] so a client rendering a datasource can badge the
//     gap without running a check first (`SC-F407`).
//   - **Every mutation is restart-class** (`SC-F409`): `datasources.reload:
//     true` is rejected at validate time and at boot, so these routes mutate the
//     **draft** only and every response states the class through
//     [DataSource.change_class] / [DataSource.restarts]. The restart happens at
//     publish, and `GET .../draft` reports the datasource class from the moment
//     one lands.
//
// Credentials never round-trip: a write carries `credentials` (write-only), a
// read returns `credentials_ref` naming the materialized `secret://` target plus
// `has_credential`, and nothing else (`SC-F405`, `SC-F326`, `SC-S409`).

import { del, getJSON, newIdempotencyKey, query, request, sendJSON } from "./http.js";
import type { JobStarted } from "./jobs.js";
import type { FormFieldOffer, FormFieldStatus, PolicyFormSchema, PolicyKind } from "./policies.js";

/** DataSourceType is the six drivers, in picker order. Fixed at creation:
 * switching type is a delete plus a create, because the required fields differ
 * entirely between them. */
export type DataSourceType =
  | "postgres"
  | "mysql"
  | "sqlite"
  | "s3"
  | "duckdb_file"
  | "motherduck";

/**
 * DATASOURCE_TYPES is the vocabulary in picker order, mirroring
 * `policymodel.DataSourceTypes()`.
 *
 * It is a **fallback**, not the source of truth: the picker is built from the
 * form schema's `DataSourceType` enum ([getDataSourceForm]), which carries the
 * per-value labels and help text and is drift-checked in CI against the pinned
 * Sluice source. This constant exists so a screen can still name the six drivers
 * when the schema request itself failed.
 */
export const DATASOURCE_TYPES: readonly DataSourceType[] = [
  "postgres",
  "mysql",
  "sqlite",
  "s3",
  "duckdb_file",
  "motherduck",
];

/**
 * FILE_DATASOURCE_ROOT is the only directory a file-backed datasource may read
 * from: the instance's mounted data volume. Sluice does not enforce this — it
 * would happily open any path the container can see — so the publish gate does
 * (linter rule L32). Mirrors `policymodel.FileDataSourceRoot`.
 */
export const FILE_DATASOURCE_ROOT = "/var/lib/sluice/files/";

/**
 * FORCE_ACKNOWLEDGEMENT is the literal `?acknowledge=` value a forced delete must
 * carry, mirroring `datasources.ForceAcknowledgement`.
 *
 * `SC-S408`'s last criterion is that a forced deletion "requires an explicit
 * acknowledgement stating that the affected policies will silently match
 * nothing". An exact literal is how that becomes a check rather than a checkbox,
 * and the string itself is the sentence the customer is agreeing to — so the
 * dialog copy and this constant are one decision.
 */
export const FORCE_ACKNOWLEDGEMENT = "policies-will-silently-match-nothing";

/**
 * NotEnforcedField is one badged field: the manifest path, the badge text, the
 * reason and the upstream citation that proves it — consumed verbatim from
 * `policymodel`'s form schema rather than restated (`SC-F501`, frozen interface
 * `I3`).
 *
 * `SC-F407` requires `spec.schemas`, `spec.tables` and `spec.healthCheck.*` to be
 * hidden **or** badged. This product badges them: a hidden field cannot teach a
 * reader that the control they were looking for does nothing.
 */
export interface NotEnforcedField {
  /** The manifest path, e.g. `spec.schemas`. */
  path: string;
  /** The short label to render next to the field. */
  badge: string;
  /** What the field does in the pinned build. */
  reason: string;
  /** The upstream code that proves it, so a reader can check the claim rather
   * than trust it. */
  source?: string;
}

/** DatasourceHealthStatus is the three values `datasources.health` stores.
 * `unknown` is a first-class value and not a null stand-in: an instance nobody
 * has probed must read as unprobed, because the honest-labels rule forbids a
 * green state for something nobody asked (`SC-S403`). */
export type DatasourceHealthStatus = "healthy" | "unhealthy" | "unknown";

/** Health is one catalog's last observation, made by the instance's own health
 * loop (`SC-F410`). */
export interface Health {
  status: DatasourceHealthStatus | string;
  /** RFC 3339, or absent for never probed. Absent renders as "never probed" and
   * never as a stale reading presented as current. */
  checked_at?: string;
  /** Omitted when the probe failed or has not run — a zero latency is not a fast
   * probe. */
  latency_ms?: number;
  /** The driver's message, VERBATIM. It is attacker-influenced text from a
   * third-party server, so it is escaped, never rendered as markup, and shown in
   * a copyable block because it is what a customer pastes into their DBA's
   * ticket (`SC-S405`). */
  error?: string;
  /** True when this row came from the control plane's own copy rather than from
   * the instance in this request. */
  stale?: boolean;
  /** The catalog, on the panel's rows; empty on a single-catalog response where
   * the enclosing object already names it. */
  name?: string;
  type?: string;
}

/** HealthUnavailable is why the panel has no live reading (`SC-S406`). */
export interface HealthUnavailable {
  /** The HTTP status the instance's admin plane answered, or 0 when no response
   * was obtained at all — a transport failure or a timeout. */
  status: number;
  /** `unreachable`, `timeout`, `unauthorized`, `not_implemented`,
   * `instance_error` or `malformed_response`. */
  reason: string;
  message: string;
  /** The `X-Admin-Request-Id` the instance echoed — the only join key between
   * this failure and a line in the instance's own log. */
  request_id?: string;
}

/**
 * HealthPanel is the whole instance's catalog health, embedded in the list so the
 * page needs one request rather than one per catalog.
 *
 * `overall` is `degraded` rather than `down` when the instance answered and one
 * catalog did not: the badge has to distinguish a broken warehouse from a broken
 * instance, which is the whole reason a support engineer opens this panel.
 */
export interface HealthPanel {
  overall: "healthy" | "degraded" | "unknown" | "unavailable" | string;
  /** False when the admin call did not answer. The rows may still be populated
   * from the control plane's copy, each marked [Health.stale]. */
  available: boolean;
  unavailable?: HealthUnavailable;
  datasources: Health[];
  /** When the CONTROL PLANE made this observation — distinct from each row's
   * `checked_at`, which is when the instance probed. */
  observed_at: string;
  /** The instance's own probe period. Polling faster renders a number that
   * cannot change (`SC-S406`). */
  refresh_after_seconds: number;
  /** Sluice's per-probe budget, so a latency close to it reads as "probably
   * timing out" rather than as an unexplained spike. */
  probe_timeout_seconds: number;
}

/**
 * DataSource is one attached catalog as every read route answers it
 * (`datasources.View`).
 *
 * `name` **is** the DuckDB catalog name and is what every `resources.catalogs`
 * pattern matches. Renaming one silently invalidates every policy that
 * referenced the old name — the policies still compile, they simply stop
 * matching — which is why `PUT` refuses a rename outright and why it is a
 * separate guarded operation.
 *
 * **There is no field a credential could be marshaled into**, and that is
 * structural rather than remembered: `SC-S409`'s first criterion is that
 * credential fields are absent from the response, not blanked or masked in place.
 */
export interface DataSource {
  name: string;
  display_name?: string;
  type: DataSourceType | string;

  connection?: string;
  path?: string;
  bucket?: string;
  prefix?: string;
  region?: string;
  allowed_paths?: string[];
  endpoint?: string;
  database?: string;
  /** Always present. `false` is the **widening** value and the wire default is
   * the permissive one, so every construction path states it explicitly. */
  readonly: boolean;
  attach_mode?: string;

  /** The materialized `secret://…` reference, never the value. Empty for a
   * file-backed datasource, and that emptiness is a statement rather than a
   * missing value: a catalog on the instance's own volume has nothing to
   * authenticate to. */
  credentials_ref?: string;
  /** Whether a credential exists, so a form offers "leave blank to keep the
   * current value" only when there IS one — without disclosing anything about it
   * (`SC-S409`). */
  has_credential: boolean;

  /** The seeded sample catalog (`SC-F416`). Badge it on every surface that shows
   * it, so an evaluator does not mistake demo data for their own. */
  sample?: boolean;

  /** The last observation, or absent when none has been read. Absent renders as
   * "unknown", never as healthy. */
  health?: Health;
  /** This catalog's introspection-cache freshness, or absent for an empty
   * cache. */
  schema?: SchemaStatus;
  /** The fields this build carries and does not act on (`SC-F407`). It is on the
   * READ shape and not only on the check result because a client that renders a
   * datasource must be able to explain the gap without running a check first. */
  not_enforced?: NotEnforcedField[];

  /** What publishing the current draft would cost for this object. It is stated
   * on every read so the UI can present datasource editing as the heavier change
   * class BEFORE the edit rather than in the confirmation dialog (`SC-F409`,
   * `SC-S404`). */
  change_class: string;
  /** The single bit a caller branches on. Always true for a datasource, whether
   * or not it has been changed yet. */
  restarts: boolean;

  created?: string;
  updated?: string;
}

/**
 * DataSourceListing is `GET …/datasources`.
 *
 * `next` is always null — an instance's catalog count is bounded by how many one
 * gateway can attach, and the page that reads this renders the whole set — and
 * the field is present so a client has one list shape to code against
 * (`06` §1.5).
 */
export interface DataSourceListing {
  items: DataSource[];
  next: string | null;
  /** The whole instance's panel (`SC-F410`). */
  health: HealthPanel;
  /** The introspection cache's freshness across every catalog, so the list can
   * show the staleness indicator without a second call (`SC-F411`). */
  schema: SchemaStatus;
  /** The badged fields, once for the whole list rather than once per row. */
  not_enforced?: NotEnforcedField[];
}

/**
 * DataSourceCredentials is the write-only credential block (`SC-F405`).
 *
 * The two payload shapes are genuinely different and conflating them is the most
 * likely mistake on this path: postgres, mysql and motherduck read the file as an
 * opaque password or token, while s3 demands a key id and a secret access
 * key. On a replace, leaving the whole block empty means "keep the current
 * credential" — and it means it at the storage level, because re-sealing an
 * unchanged password would mint a new content address and a new content address
 * restarts the instance.
 */
export interface DataSourceCredentials {
  /** The password for postgres and mysql, the token for motherduck, or the secret
   * access key for s3. */
  secret?: string;
  /** s3 only. */
  key_id?: string;
  /** s3 only. */
  session_token?: string;
}

/**
 * WriteDataSourceInput is the create and replace body (`datasources.Form`) — the
 * six connection forms as one document, discriminated by `type`.
 *
 * Per-type requiredness is deliberately **not** encoded in this type: it is a
 * property of the form schema (`I3`), which the route reads too, so the
 * requiredness the browser enforces and the requiredness the API enforces are one
 * declaration rather than two that agree today. See [getDataSourceForm].
 */
export interface WriteDataSourceInput {
  name: string;
  /** Control-plane-only sugar. It reaches no manifest, so it is the one field on
   * this form whose change is not a restart. */
  display_name?: string;
  type: DataSourceType;

  /** postgres, mysql. The route **refuses** an embedded password: Sluice reads
   * the password only from the sealed credential, and a DSN password would be
   * stored in a rendered policy file that is not encrypted at rest and returned by
   * every read. */
  connection?: string;
  /** sqlite, duckdb_file. Must be a plain file directly under
   * [FILE_DATASOURCE_ROOT]; the value is used verbatim and never cleaned. */
  path?: string;

  bucket?: string;
  prefix?: string;
  region?: string;
  /** s3. Adding entries **widens** access and is flagged by the diff risk
   * classifier. An empty pattern is refused outright: in Sluice's wildcard grammar
   * it matches nothing, so the entry would silently narrow access to zero paths. */
  allowed_paths?: string[];
  endpoint?: string;

  database?: string;

  /** Absent means `policymodel.DefaultDataSourceReadonly`. Send it explicitly:
   * the Go zero value is the widening one, so "the client omitted the field" and
   * "the client asked for a writable attach" must not be the same request. */
  readonly?: boolean;
  /** Constrained to `readonly`. Sluice accepts `readwrite` and does not validate
   * it away, so refusing it is a product decision — `blocked-by-us` in the form
   * schema's status vocabulary. */
  attach_mode?: string;

  /** Write-only, with no counterpart on [DataSource]. Omit on a replace to keep
   * the existing credential. */
  credentials?: DataSourceCredentials;
}

const base = (instanceId: string) => `/v1/instances/${encodeURIComponent(instanceId)}/datasources`;

/** listDatasources returns the instance's datasources with the health panel and
 * the schema cache's freshness (`SC-F410`, `SC-F411`). */
export function listDatasources(instanceId: string): Promise<DataSourceListing> {
  return getJSON<DataSourceListing>(base(instanceId));
}

/** getDatasource reads one datasource from the DRAFT when there is one and from
 * the active generation otherwise — the customer is looking at what they are
 * editing. Credentials are never returned. */
export function getDatasource(instanceId: string, name: string): Promise<DataSource> {
  return getJSON<DataSource>(`${base(instanceId)}/${encodeURIComponent(name)}`);
}

/** createDatasource adds a datasource **to the draft** (`SC-F400`–`SC-F404`).
 * Maintainer and above; a `422` carries `details.fields` with the per-type
 * requiredness the form schema declares. */
export function createDatasource(
  instanceId: string,
  input: WriteDataSourceInput,
): Promise<DataSource> {
  return sendJSON<DataSource>(base(instanceId), "POST", input, {
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * replaceDatasource replaces the whole record in the draft.
 *
 * Two changes it refuses, because accepting either would produce a datasource
 * that validates and then does not work: the **type** (a switch is a delete plus
 * a create, since the required fields differ entirely between the six drivers)
 * and the **name** (it is the catalog name every `resources.catalogs` pattern
 * matches, so a rename silently stops those policies matching).
 */
export function replaceDatasource(
  instanceId: string,
  name: string,
  input: WriteDataSourceInput,
  opts: { ifMatch?: string } = {},
): Promise<DataSource> {
  return sendJSON<DataSource>(
    `${base(instanceId)}/${encodeURIComponent(name)}`,
    "PUT",
    input,
    opts,
  );
}

/**
 * CatalogReference is one policy object that would stop matching if a catalog
 * went away — `details.references` on the `409 conflict` [deleteDatasource]
 * answers with `details.reason = "catalog_referenced"` (`SC-F413`).
 *
 * The list is the whole feature rather than a nicety: an unknown catalog pattern
 * in Sluice does not error, it simply never matches, so the failure mode of an
 * unchecked delete is protection that appears configured and is inert.
 */
export interface CatalogReference {
  kind: PolicyKind | string;
  name: string;
  /** The `resources.catalogs` value as WRITTEN, wildcards included. `SC-S408`
   * requires it be shown: `analytics_*` matching `analytics_eu` is only obviously
   * a reference once the reader sees the pattern. */
  pattern: string;
  /** True for a pattern reference, so a UI can say "this policy names the
   * catalog" and "this policy's pattern covers the catalog" differently. */
  wildcard?: boolean;
  /** The pointer into the spec that produced the reference, so the UI can jump to
   * the field rather than making the author hunt for it. */
  path?: string;
}

/**
 * deleteDatasource removes it from the draft.
 *
 * **Guarded**: `409 conflict` with `details.references` while a policy still
 * references the catalog (`SC-F413`). `force` bypasses the guard and sends
 * [FORCE_ACKNOWLEDGEMENT] — the acknowledgement travels in the query rather than
 * a body because a DELETE body is not reliably forwarded by every proxy, and an
 * acknowledgement a proxy can strip is worse than none.
 */
export function deleteDatasource(
  instanceId: string,
  name: string,
  opts: { force?: boolean } = {},
): Promise<void> {
  const q = opts.force ? query({ force: true, acknowledge: FORCE_ACKNOWLEDGEMENT }) : "";
  return del(`${base(instanceId)}/${encodeURIComponent(name)}${q}`);
}

/**
 * rotateCredential replaces a datasource's credential (`SC-F406`, v1) → job.
 * Step-up gated: rotation is restart-class and a hostile rotation is an
 * availability attack that looks like routine maintenance.
 *
 * **Staged.** The route is declared in `06` §3.6 and deliberately not served, so
 * this answers `404` today. Until it lands, the honest path is a replace carrying
 * a fresh `credentials` block, which is what the editor does.
 */
export function rotateCredential(
  instanceId: string,
  name: string,
  credentials: DataSourceCredentials,
  opts: { verification?: string } = {},
): Promise<JobStarted> {
  return sendJSON<JobStarted>(
    `${base(instanceId)}/${encodeURIComponent(name)}/rotate`,
    "POST",
    { credentials },
    { idempotencyKey: newIdempotencyKey(), verification: opts.verification },
  );
}

/**
 * CheckedDataSource is one row of the configuration check.
 *
 * `schemas` and `tables` are **counts**, not names, and that is itself part of
 * the honest labelling: both lists are carried into the driver spec and consulted
 * by no driver, so publishing the names would invite a client to render them as
 * an effective filter. They are non-zero only for an imported manifest — this
 * product's forms cannot set them at all.
 */
export interface CheckedDataSource {
  name: string;
  type: string;
  /** The whole of what the command decides. */
  factory_known: boolean;
  schemas: number;
  tables: number;
  /** The command's own message, verbatim and untranslated. For an unknown type it
   * already names the type and lists the instance's registered set, which is what
   * makes `SC-S403`'s third criterion satisfiable from the authority rather than
   * from our model. Escape it; never render it as markup. */
  error?: string;
}

/**
 * CheckReport is the answer to `POST …/datasources/check` (`SC-F407`,
 * `SC-F710`) — `datasources.CheckReport`.
 *
 * Read `checked`, `not_checked` and `notice` and render them: they are the
 * contract that keeps this surface honest about opening no socket. There is no
 * latency, no `reachable` and no `probed_at` in this type, and that absence is
 * the feature.
 */
export interface CheckReport {
  /** `failed === 0` as the command computed it. It means "every declared type has
   * a driver in this build", and nothing else. */
  ok: boolean;
  /** "spec-level only: a registered driver factory exists for each declared
   * type". */
  checked: string;
  /** What a reader must **not** conclude from `ok: true`: network reachability,
   * credentials, schema and table filters. */
  not_checked: string[];
  datasources: CheckedDataSource[];
  failed: number;
  /** The top-level spec names that parse and do nothing — `schemas`, `tables`,
   * `healthCheck` (`SC-F407`). */
  not_enforced: string[];
  /** The six drivers this control plane models, offered so a UI can list the
   * vocabulary when a row reports `factory_known: false`. The INSTANCE's registry
   * is authoritative and its own list appears verbatim inside the row's `error`;
   * this is the fallback, not a second opinion. */
  known_types: string[];
  /** The sentence the completion screen shows verbatim: what was checked, and
   * where connectivity is actually proven. */
  notice: string;
}

/**
 * checkDatasources runs `sluice datasource check --json` over the draft as a
 * one-shot task on the instance's pinned image (`SC-F407`, `SC-F710`).
 *
 * **Label it "Validate configuration" everywhere.** It opens no connection, and
 * `SC-F407` makes saying so a "done when" criterion.
 *
 * It is synchronous — the route answers `200` with the report rather than a job —
 * so the wizard's verification step needs no poll loop. A deployment with no
 * proxied-CLI substrate answers `proxy_unavailable` rather than silently
 * substituting the control plane's own weaker answer under the same shape.
 */
export function checkDatasources(instanceId: string): Promise<CheckReport> {
  return sendJSON<CheckReport>(
    `${base(instanceId)}/check`,
    "POST",
    {},
    { idempotencyKey: newIdempotencyKey() },
  );
}

/** ProbeResult is the `datasource.probe` job result: a genuine live connection
 * attempt (`SC-F408`, v1, blocked on `SLUICE-U05`). This one **may** be called a
 * connection test. **Staged**: the route is declared and not served. */
export interface ProbeResult {
  name: string;
  reachable: boolean;
  latency_ms: number | null;
  error: string | null;
}

/** probeDatasource opens a real connection to one datasource → job. **Staged**;
 * see [ProbeResult]. */
export function probeDatasource(
  instanceId: string,
  name: string,
): Promise<JobStarted<ProbeResult>> {
  return sendJSON<JobStarted<ProbeResult>>(
    `${base(instanceId)}/${encodeURIComponent(name)}/probe`,
    "POST",
    {},
    { idempotencyKey: newIdempotencyKey() },
  );
}

/**
 * getDatasourceHealth reads one catalog's health, as a [HealthPanel] carrying
 * that one row plus the panel-level `unavailable` explanation (`SC-F410`).
 *
 * An instance that answered and does not know the catalog returns an empty
 * `datasources` list — a real state, for a datasource that is in the draft and
 * not published yet — rather than an invented status.
 */
export function getDatasourceHealth(instanceId: string, name: string): Promise<HealthPanel> {
  return getJSON<HealthPanel>(`${base(instanceId)}/${encodeURIComponent(name)}/health`);
}

// ─────────────────────────────────────────────── schema introspection ──

/** SchemaColumn is one column of the introspection cache (`SC-F411`). */
export interface SchemaColumn {
  name: string;
  type: string;
}

/** SchemaTableNode is one table inside a schema node. */
export interface SchemaTableNode {
  name: string;
  columns: SchemaColumn[];
}

/** SchemaNode is one schema inside a catalog's tree. */
export interface SchemaNode {
  name: string;
  tables: SchemaTableNode[];
}

/** SchemaTree is one catalog's cached tree. `truncated` marks a tree that did not
 * fit the store's cap, so a picker can say "autocomplete is incomplete" rather
 * than letting an author conclude a missing column does not exist. */
export interface SchemaTree {
  schemas: SchemaNode[];
  truncated?: boolean;
}

/**
 * SchemaStatus is the freshness of one catalog's cache, or the worst freshness
 * across a response.
 *
 * `degraded` is the single bit every completion field honours: true whenever the
 * field must fall back to free text, and a client that honours it **never blocks
 * authoring** (`SC-S407`). `fresh: false` with a non-empty `fetched_at` is
 * staleness; `fresh: false` with an empty one is an empty cache.
 */
export interface SchemaStatus {
  fetched_at?: string;
  /** `mcp_list_tables`, `admin_api` or `manual` — three different levels of
   * trustworthiness, and the UI says which one it is showing. */
  source?: string;
  fresh: boolean;
  truncated?: boolean;
  object_count?: number;
  fresh_for_seconds: number;
  degraded: boolean;
  /** The sentence to show alongside a degraded or stale cache. */
  warning?: string;
}

/** CatalogSchema is one catalog's cached tree plus that catalog's own freshness. */
export interface CatalogSchema {
  catalog: string;
  type: string;
  sample?: boolean;
  tree: SchemaTree;
  status: SchemaStatus;
}

/**
 * SchemaResponse is `GET /v1/instances/{id}/schema`.
 *
 * It **never** fails because the cache is empty or stale — an empty cache is a
 * `200` whose `status.degraded` is true and whose `warning` carries the sentence
 * to show. A 404 or 409 here would block authoring, which `SC-S407` forbids.
 *
 * The `?schema=` and `?table=` filters narrow by EXACT name, not by wildcard:
 * resolving a wildcard server-side would be a second, partial implementation of
 * Sluice's matcher. The honest answer for a wildcard is `SC-F507`'s pattern
 * preview computed over the whole tree.
 */
export interface SchemaResponse {
  catalogs: CatalogSchema[];
  /** The WORST freshness across the returned catalogs, so a client rendering one
   * badge for the whole picker renders the honest one. */
  status: SchemaStatus;
}

/** SchemaTable is one table flattened out of the tree and fully qualified — the
 * shape a pattern preview and a column picker want. */
export interface SchemaTable {
  catalog: string;
  schema: string;
  table: string;
  columns: SchemaColumn[];
}

/** getSchema reads the introspection cache, optionally narrowed. */
export function getSchema(
  instanceId: string,
  params: { catalog?: string; schema?: string; table?: string } = {},
): Promise<SchemaResponse> {
  return getJSON<SchemaResponse>(
    `/v1/instances/${encodeURIComponent(instanceId)}/schema${query({ ...params })}`,
  );
}

/** flattenSchema walks a [SchemaResponse] into fully-qualified tables. It lives
 * here rather than in a page because the tree's nesting is a storage decision —
 * one JSON document per catalog, so a refresh is not tens of thousands of rows —
 * and every consumer wants the flat form. */
export function flattenSchema(resp: SchemaResponse): SchemaTable[] {
  const out: SchemaTable[] = [];
  for (const c of resp.catalogs) {
    for (const s of c.tree.schemas ?? []) {
      for (const t of s.tables ?? []) {
        out.push({ catalog: c.catalog, schema: s.name, table: t.name, columns: t.columns ?? [] });
      }
    }
  }
  return out;
}

// ──────────────────────────────────────────── the schema browser (SC-F412) ──

/** BrowseLevel is which rung of the cached tree a browse request reads. `search`
 * is the flat mode: table — and optionally column — names across every catalog. */
export type BrowseLevel = "catalogs" | "schemas" | "tables" | "columns" | "search";

/** The five levels in tree order. */
export const BROWSE_LEVELS: BrowseLevel[] = [
  "catalogs",
  "schemas",
  "tables",
  "columns",
  "search",
];

/** MIN_SEARCH_QUERY mirrors `datasources.MinSearchQuery`. A shorter term is
 * refused rather than silently widened: one character over a large catalog matches
 * thousands of names, the response is capped, and an arbitrary prefix of an
 * arbitrary set reads exactly like a complete answer. */
export const MIN_SEARCH_QUERY = 2;

/**
 * BrowseNode is one entry at the requested level.
 *
 * The qualifying fields are populated whenever they are an ANCESTOR of the node, so
 * a row carries its own full path and a client can build a
 * `catalog.schema.table` reference without tracking where it navigated from —
 * which the search level needs, because its rows come from everywhere.
 */
export interface BrowseNode {
  name: string;
  catalog?: string;
  schema?: string;
  table?: string;
  /** The driver type on a catalog node, the SQL type on a column node. The two
   * never share a level, so one field carries both. */
  type?: string;
  /** How many entries the next level down holds. Absent on a column node, which
   * has no next level — a zero there would read as "this column has nothing under
   * it", which is not the same statement. */
  children?: number;
  /** A catalog created by the seeded demo dataset (`SC-F416`), so the browser can
   * badge it as sample data rather than letting a reader mistake it for theirs. */
  sample?: boolean;
  /** The freshness of the cache entry this node came from. It is per node because
   * a browse can mix catalogs with different ages — a search always does. */
  status: SchemaStatus;
}

/**
 * BrowseResponse is `GET /v1/instances/{id}/schema/browse`.
 *
 * `truncated` here is a PAGING fact and is recoverable by asking for a bigger
 * limit; `status.truncated` is the stored tree having been cut to fit the cache's
 * cap, which is missing data no limit can recover. The two are separate fields
 * because the remedies differ, and a client that conflated them would offer the
 * wrong one.
 */
export interface BrowseResponse {
  level: BrowseLevel;
  catalog?: string;
  schema?: string;
  table?: string;
  nodes: BrowseNode[];
  /** How many entries matched before the limit applied. */
  total: number;
  truncated?: boolean;
  status: SchemaStatus;
  /** The sentence to show with the age. It travels with the data rather than
   * living in documentation, because `SC-F412`'s failure mode is a confident
   * reader: a stale schema presented as current is how a pattern preview lies. */
  basis: string;
}

/** BrowseParams is the browser's level, path, filter and cap. */
export interface BrowseParams {
  level?: BrowseLevel;
  catalog?: string;
  schema?: string;
  table?: string;
  /** Case-insensitive substring. On a navigation level it filters; on `search` it
   * is the term. Never a wildcard — resolving Sluice's grammar here would be a
   * second, partial implementation of its matcher. */
  q?: string;
  limit?: number;
  /** On `search`, match column names too. Opt-in, because a column search that
   * silently also matched tables would make "which tables hold a column called
   * `ssn`" unanswerable. */
  columns?: boolean;
}

/**
 * browseSchema reads one level of the cached schema (`SC-F412`).
 *
 * It never introspects. Refreshing costs a round trip into the customer's own
 * gateway and is the separate, rate-limited [refreshSchema]; a browser that re-read
 * on every navigation click would turn one curious user into a fan-out of calls
 * against a production warehouse.
 *
 * An empty or stale cache is a `200` with a degraded status, exactly as
 * [getSchema]. A 422 means the REQUEST was incoherent — an unknown level, a level
 * addressing one table without naming it, a search term below [MIN_SEARCH_QUERY] —
 * and is the one case the browser refuses, because an empty list in answer to a
 * forgotten `schema` parameter would read as "this catalog has no tables".
 */
export function browseSchema(instanceId: string, params: BrowseParams = {}): Promise<BrowseResponse> {
  return getJSON<BrowseResponse>(
    `/v1/instances/${encodeURIComponent(instanceId)}/schema/browse${query({
      level: params.level,
      catalog: params.catalog,
      schema: params.schema,
      table: params.table,
      q: params.q,
      limit: params.limit,
      columns: params.columns ? "true" : undefined,
    })}`,
  );
}

/** refreshSchema forces a re-introspection → job. Rate-limited: it is a real
 * round trip to every attached catalog. */
export function refreshSchema(instanceId: string): Promise<JobStarted> {
  return sendJSON<JobStarted>(
    `/v1/instances/${encodeURIComponent(instanceId)}/schema/refresh`,
    "POST",
    {},
    { idempotencyKey: newIdempotencyKey() },
  );
}

// ──────────────────────────────────────── files on the state volume ──
//
// `SC-F415`. Without this path two of the six driver types are configurable and
// impossible to make work: `sqlite` and `duckdb_file` need a file at a fixed path
// on a node-local volume the customer has no shell on. The mechanism is the same
// node-pinned one-shot writer the publish path uses — no standing agent, no
// interactive shell, no Docker socket.

/**
 * PlacedFile is one file on the instance's state volume.
 *
 * `path` is the absolute in-container target, which is exactly the value a
 * `sqlite` or `duckdb_file` datasource's `path` must carry — so the picker offers
 * a value the form will accept rather than asking the customer to compose one
 * (`SC-S410`).
 */
export interface PlacedFile {
  name: string;
  path: string;
  size_bytes: number;
  digest: string;
  sample?: boolean;
  placed_by?: string;
  job_id?: string;
  created?: string;
  updated?: string;
  /** The datasources whose `path` points at this file, so a delete can warn
   * before it strands a catalog — the same class of silent failure `SC-F413`
   * guards in the other direction. */
  used_by?: string[];
}

/** FileListing is `GET …/files`. `root` and `max_bytes` are returned so a client
 * explains the constraint and refuses an oversized upload before spending the
 * customer's bandwidth, without composing the path or hard-coding the ceiling. */
export interface FileListing {
  items: PlacedFile[];
  next: string | null;
  root: string;
  max_bytes: number;
}

/** listFiles reads the placed files — the `path` picker's source (`SC-F415`). */
export function listFiles(instanceId: string): Promise<FileListing> {
  return getJSON<FileListing>(`/v1/instances/${encodeURIComponent(instanceId)}/files`);
}

/**
 * PlaceFileResult is what a successful placement answers.
 *
 * `unchanged` and `restart_required` are two different facts with two different
 * consequences: an identical replacement costs no restart, and telling a customer
 * otherwise would train them to schedule maintenance they do not need. `notice`
 * is the sentence to show verbatim.
 */
export interface PlaceFileResult {
  file: PlacedFile;
  job_id?: string;
  replaced: boolean;
  unchanged: boolean;
  /** DuckDB attaches a file at process start, so until the instance restarts it
   * keeps serving the PREVIOUS contents (`SC-S410`). */
  restart_required: boolean;
  notice?: string;
}

/**
 * placeFile streams one file onto the instance's state volume (`SC-F415`).
 *
 * The body is the bytes, not a multipart envelope: the name is a query parameter
 * and the content type is opaque, which keeps the control plane from having to
 * parse — and therefore buffer — a form. `replace` is the acknowledgement that
 * replacing a file in place is restart-class.
 */
export async function placeFile(
  instanceId: string,
  name: string,
  file: Blob,
  opts: { replace?: boolean } = {},
): Promise<PlaceFileResult> {
  const q = query({ name, replace: opts.replace ? true : undefined });
  const res = await request(`/v1/instances/${encodeURIComponent(instanceId)}/files${q}`, {
    method: "POST",
    headers: { "Content-Type": "application/octet-stream" },
    body: file,
  });
  return (await res.json()) as PlaceFileResult;
}

/** deleteFile removes a placed file from the volume. */
export function deleteFile(instanceId: string, name: string): Promise<void> {
  return del(`/v1/instances/${encodeURIComponent(instanceId)}/files/${encodeURIComponent(name)}`);
}

// ────────────────────────────────────────────────── sample catalog ──

/**
 * SampleResult is what attaching the sample catalog answers (`SC-F416`).
 *
 * The datasource it returns is an **ordinary** `DataSource` — readable, editable
 * and deletable — because the sample must not be a hidden or privileged object
 * (`SC-S411`).
 */
export interface SampleResult {
  file: PlacedFile;
  datasource: DataSource;
  /** What the dataset is, from the deployment's own sample provider. */
  description: string;
  /** The sentence to show verbatim: what it is, that every surface badges it as
   * sample data, and that it becomes queryable only when the instance next
   * starts — because attaching a catalog is a restart. */
  notice: string;
}

/** attachSampleCatalog places the seeded sample dataset and registers it as a
 * `duckdb_file` datasource in one action (`SC-F416`). A deployment that ships no
 * sample answers `409 conflict` with `details.reason = "sample_unavailable"`. */
export function attachSampleCatalog(instanceId: string): Promise<SampleResult> {
  return sendJSON<SampleResult>(
    `/v1/instances/${encodeURIComponent(instanceId)}/sample-catalog`,
    "POST",
    {},
    { idempotencyKey: newIdempotencyKey() },
  );
}

/** removeSampleCatalog removes the sample datasource **and** its file in one
 * action (`SC-F416`). `force` carries [FORCE_ACKNOWLEDGEMENT], because the sample
 * can be referenced by a policy like any other catalog. */
export function removeSampleCatalog(
  instanceId: string,
  opts: { force?: boolean } = {},
): Promise<void> {
  const q = opts.force ? query({ force: true, acknowledge: FORCE_ACKNOWLEDGEMENT }) : "";
  return del(`/v1/instances/${encodeURIComponent(instanceId)}/sample-catalog${q}`);
}

// ───────────────────────────────────────────── the form schema (I3) ──
//
// Frozen interface `I3`: **the UI codes against the form schema, not against the
// Go types.** `GET /v1/policy-schema` answers `policymodel.FormSchemaJSON()` —
// the document itself — and `api/policies.ts` leaves its field entries
// deliberately open because "editors narrow what they read, in one place each".
//
// This is the DataSource editor's narrowing, and it lives in this module for the
// same reason `internal/datasources/form.go` reads the schema itself rather than
// restating it: the datasource form's per-type requiredness, its closed enums and
// its badged fields are ONE declaration, walked by the route and by the browser.

/** FormFieldCondition is the schema's one condition shape — "this sibling is in
 * this set". It is deliberately not an expression language: a second evaluator in
 * the browser is a second thing that can disagree with the binary. */
export interface FormFieldCondition {
  /** The discriminator's path, relative to the object the annotated field lives
   * in. On DataSource it is always `spec.type`. */
  field: string;
  in: string[];
}

/**
 * FormField is one control of the form schema, narrowed to what a datasource form
 * reads. Keys this editor does not consult — `pattern`, `minItems`,
 * `discriminatedBy` — are deliberately absent rather than mirrored: a partial
 * mirror nobody reads is a second source of truth waiting to drift.
 */
export interface FormField {
  name: string;
  type: string;
  format?: string;
  /** Names an entry in the schema's `enums` registry. */
  enum?: string;
  /** Names the field set this object's children come from. */
  group?: string;
  items?: { type: string; format?: string; enum?: string; group?: string };
  widget?: string;
  help?: string;
  required?: boolean;
  /** `sluice` when the decoder or compiler enforces it, `us` when the product
   * does. The distinction is what makes drift symmetric. */
  requiredBy?: string;
  /** Per-type requiredness — `connection` versus `path` versus `bucket` versus
   * `database`. This is the thing the upstream schema export cannot say at all. */
  requiredWhen?: FormFieldCondition;
  /** Hides a field that has no meaning for the current discriminator value,
   * without claiming it is invalid. */
  appliesWhen?: FormFieldCondition;
  /** What Sluice assumes when the field is absent. */
  default?: unknown;
  /** What the editor pre-selects, which is a product decision and differs from
   * `default` wherever our opinion differs from Sluice's silence. */
  initial?: unknown;
  maxLength?: number;
  /** Empty for the ordinary case. Anything else decides whether the field is
   * offered, badged, disabled or hidden. */
  status?: FormFieldStatus;
  /** The `SLUICE-U##` ask that would change the status. */
  upstream?: string;
  badge?: string;
  reason?: string;
  source?: string;
}

/** FormEnumValue is one member of a closed vocabulary, with the help text that
 * makes a bare identifier meaningful and the status that decides whether it may
 * be picked at all. */
export interface FormEnumValue {
  value: string;
  label: string;
  help?: string;
  status?: FormFieldStatus;
  upstream?: string;
  reason?: string;
  source?: string;
  /** The value Sluice assumes when the field is absent — not the same as the one
   * the editor pre-selects. */
  default?: boolean;
}

/** FormEnum is one closed vocabulary. `validatedUpstream: false` is the dangerous
 * case: Sluice takes the value verbatim and never checks it, so a typo compiles
 * cleanly and then silently never matches. */
export interface FormEnum {
  origin: string;
  goType: string;
  source: string;
  validatedUpstream: boolean;
  help?: string;
  values: FormEnumValue[];
}

/** FormStatusDoc is one row of the schema's status table: the badge text and the
 * sentence behind it, so one vocabulary — "not read in this build", "not enforced
 * in this build" — is used on every surface in the product. */
export interface FormStatusDoc {
  status: FormFieldStatus;
  badge: string;
  help: string;
  offer: FormFieldOffer;
}

/** FormConstraint is a cross-field rule the schema states and that a browser may
 * have to explain without being able to run it. `enforcedBy` is `sluice` or `us`,
 * and `linter` names the `SC-F529` rule that checks it where there is one. */
export interface FormConstraint {
  id: string;
  severity: string;
  fields: string[];
  statement: string;
  enforcedBy: string;
  feature?: string;
  linter?: string;
}

/** ResolvedFormField is one field at its full manifest path, mirroring
 * `policymodel.ResolvedField`: a linter finding and a form control address the
 * same string. */
export interface ResolvedFormField {
  path: string;
  field: FormField;
  depth: number;
}

/**
 * DataSourceForm is the DataSource kind's form definition, resolved.
 *
 * `fields` is every field at its full path, parents before children, in document
 * order — the order a form renders and the order the YAML view emits. `enums`,
 * `statuses` and `constraints` are what those fields point into.
 */
export interface DataSourceForm {
  /** The document's version and the Sluice commit it was checked against
   * (`OD-01`). */
  formSchemaVersion: string;
  sluiceCommit: string;
  /** Provenance prose — "hand-maintained (SC-F501); drift-checked in CI …". */
  source: string;
  /** The `SLUICE-U##` ask that would remove the hand maintenance. */
  upstream: string;
  /** What the schema does not carry, because `sluice schema export` cannot say
   * it. `SC-F501` requires it be reachable somewhere in the UI. */
  knownGaps: string[];
  title: string;
  help: string;
  /** True when any change to this kind is restart-class (`SC-F409`) — always true
   * for DataSource, and the reason the editor says so before the edit. */
  restart: boolean;
  fields: ResolvedFormField[];
  enums: Record<string, FormEnum>;
  statuses: FormStatusDoc[];
  constraints: FormConstraint[];
}

/** The schema is immutable for the life of the deployment — it is versioned with
 * the pinned Sluice — and every editor needs it, so one in-flight request is
 * shared and its answer kept. A failure is NOT cached: the next open retries. */
let formSchemaRequest: Promise<PolicyFormSchema> | null = null;

/** fetchFormSchema reads `GET /v1/policy-schema`, which answers the schema
 * document itself rather than an envelope around it. */
function fetchFormSchema(): Promise<PolicyFormSchema> {
  formSchemaRequest ??= getJSON<PolicyFormSchema>("/v1/policy-schema").catch((err: unknown) => {
    formSchemaRequest = null;
    throw err;
  });
  return formSchemaRequest;
}

/**
 * getDataSourceForm fetches the form schema and returns the DataSource kind
 * resolved: every field at its full path, with the enum and status registries it
 * refers to.
 *
 * The resolution mirrors `policymodel.Schema.Fields` exactly — group `include`
 * merging, `parent.child` path composition, `[]` on an array's children and the
 * recursion guard — because a form built from a different expansion of the same
 * document would offer different fields than the route validates.
 */
export async function getDataSourceForm(): Promise<DataSourceForm> {
  const doc = await fetchFormSchema();
  const kind = (doc.kinds ?? []).find((k) => k.kind === "DataSource");
  if (!kind) {
    // A schema carrying no DataSource kind is a broken deployment artifact, not a
    // request failure, and it must not degrade into an empty form: an empty form
    // offers nothing and refuses nothing.
    throw new Error("the form schema carries no DataSource kind");
  }
  const groups = (doc.groups ?? {}) as Record<string, FormGroupDoc>;
  const fields: ResolvedFormField[] = [];
  walkFormFields(groups, "", (kind.fields ?? []) as FormField[], 0, [], fields);
  const provenance = doc as unknown as { source?: string; upstream?: string };
  return {
    formSchemaVersion: doc.formSchemaVersion,
    sluiceCommit: doc.sluiceCommit,
    source: provenance.source ?? "",
    upstream: provenance.upstream ?? "",
    knownGaps: doc.knownGaps ?? [],
    title: kind.title,
    help: kind.help,
    restart: kind.restart,
    fields,
    enums: (doc.enums ?? {}) as Record<string, FormEnum>,
    statuses: (doc.statuses ?? []) as FormStatusDoc[],
    constraints: (kind.constraints ?? []) as unknown as FormConstraint[],
  };
}

/** FormGroupDoc is a reusable field set as the document stores it. */
interface FormGroupDoc {
  include?: string[];
  help?: string;
  fields?: FormField[];
}

/** resolveFormGroup merges a group's `include`d field lists in front of its own,
 * which is how the shared envelope is defined once instead of on every kind. */
function resolveFormGroup(
  groups: Record<string, FormGroupDoc>,
  name: string,
): FormField[] | null {
  const group = groups[name];
  if (!group) return null;
  if (!group.include || group.include.length === 0) return group.fields ?? [];
  const merged: FormField[] = [];
  for (const inc of group.include) merged.push(...(groups[inc]?.fields ?? []));
  merged.push(...(group.fields ?? []));
  return merged;
}

/** walkFormFields expands one field list. `stack` carries the group names being
 * expanded, which is what stops a self-referential group — the predicate tree —
 * from expanding forever. */
function walkFormFields(
  groups: Record<string, FormGroupDoc>,
  prefix: string,
  fields: FormField[],
  depth: number,
  stack: string[],
  out: ResolvedFormField[],
): void {
  for (const field of fields) {
    const path = prefix ? `${prefix}.${field.name}` : field.name;
    const group = field.items?.group || field.group || "";
    const recursive = group !== "" && stack.includes(group);
    out.push({ path, field, depth });
    if (group === "" || recursive) continue;
    const child = resolveFormGroup(groups, group);
    if (!child) continue;
    walkFormFields(
      groups,
      field.type === "array" ? `${path}[]` : path,
      child,
      depth + 1,
      [...stack, group],
      out,
    );
  }
}

/**
 * fieldOffer reads what the schema permits a control carrying this field's status
 * to do: `yes` for an ordinary field, `badged` for one offered with a warning,
 * `no` for one that must be visible and disabled, `hidden` for one that is not
 * rendered at all.
 *
 * It resolves through the schema's own status table rather than through a switch
 * here, because the table is the single vocabulary every surface uses. A status
 * the table does not carry resolves to `no` rather than `yes`: an unknown status
 * has to fail towards "do not offer".
 */
export function fieldOffer(form: DataSourceForm, field: FormField): FormFieldOffer {
  if (!field.status) return "yes";
  return form.statuses.find((s) => s.status === field.status)?.offer ?? "no";
}

/** fieldBadge is the badge text for a field, preferring the field's own override
 * and falling back to its status's table entry. A not-enforced field with no
 * badge anywhere still gets one: silently omitting it would let the field be read
 * as working. */
export function fieldBadge(form: DataSourceForm, field: FormField): string {
  if (field.badge) return field.badge;
  if (!field.status) return "";
  return form.statuses.find((s) => s.status === field.status)?.badge ?? "not enforced";
}

/** fieldStatusHelp is the sentence behind the badge, from the schema's status
 * table. */
export function fieldStatusHelp(form: DataSourceForm, field: FormField): string {
  if (!field.status) return "";
  return form.statuses.find((s) => s.status === field.status)?.help ?? "";
}

/**
 * matchesDiscriminator evaluates one schema condition against the chosen driver.
 *
 * A condition naming anything other than the type discriminator is treated as NOT
 * matching, exactly as `internal/datasources` does: guessing would be worse than
 * declining, and a new discriminator has to be a change in both places.
 */
export function matchesDiscriminator(
  cond: FormFieldCondition | undefined,
  type: string,
): boolean {
  if (!cond) return false;
  if (cond.field !== "spec.type" && cond.field !== "type") return false;
  return cond.in.includes(type);
}
